import {cn} from '@tempmee/core'
import {type SVGProps, forwardRef, useId} from 'react'

export type LogoTone = 'dark' | 'light'
export type LogoMark = 'full' | 'monogram'
export type LogoAppearance = 'solid' | 'gradient'

export interface LogoProps extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  /**
   * Multi-color brand gradient or a single solid color. `solid` + `tone="light"`
   * is the **flat white** lockup used on the deep-teal brand bar/rail.
   * @default 'gradient'
   */
  appearance?: LogoAppearance
  /** Full wordmark ("gotü") or just the circular monogram. @default 'full' */
  mark?: LogoMark
  /** Dark uses near-black/teal letterforms; light uses white for dark backgrounds. @default 'dark' */
  tone?: LogoTone
  /** @deprecated Use `tone` instead. */
  variant?: LogoTone
}

// --- Brand colors (Figma "Design System Shift") -----------------------------
/** Single (solid) dark letterforms. */
const NEAR_BLACK = '#1E282D'
/** Full (gradient) dark letterforms. */
const DEEP_TEAL = '#23555A'
/** Full (gradient) dark monogram ü + eyes. */
const FEATURE_TEAL = '#4FC0CF'
const WHITE = '#FFFFFF'

type GradientStop = readonly [offset: number, color: string]
interface GradientDef {
  x1: number
  y1: number
  x2: number
  y2: number
  stops: readonly GradientStop[]
}

// Vertical brand gradients, transcribed per-mark/per-tone from the Figma SVGs.
const WORDMARK_GRADIENT: Record<LogoTone, GradientDef> = {
  dark: {
    x1: 157.5,
    y1: 16.0023,
    x2: 158.5,
    y2: 97.0023,
    stops: [
      [0, '#2CCBBE'],
      [0.21875, '#2CCBBE'],
      [0.4375, '#7ED957'],
      [0.576923, '#7ED957'],
      [0.97, '#23555A'],
    ],
  },
  light: {
    x1: 171.455,
    y1: 98.6251,
    x2: 168.5,
    y2: -4.60351,
    stops: [
      [0, '#7ED957'],
      [0.48, '#00C3D2'],
      [0.97, '#CCFBF1'],
    ],
  },
}

const MONOGRAM_GRADIENT: Record<LogoTone, GradientDef> = {
  dark: {
    x1: 39.2026,
    y1: -0.00511975,
    x2: 39.2026,
    y2: 80.0051,
    stops: [
      [0, '#7ED957'],
      [0.22, '#6ACE87'],
      [0.48, '#51C1C8'],
      [0.7, '#3B9EB2'],
      [0.97, '#1E6E95'],
    ],
  },
  light: {
    x1: 39.2032,
    y1: 0.0150457,
    x2: 39.2032,
    y2: 79.9852,
    stops: [
      [0, '#C2EAD1'],
      [0.08, '#B7E6D0'],
      [0.2, '#9DDCD0'],
      [0.37, '#71CCCF'],
      [0.48, '#4FC0CF'],
      [0.64, '#5CC7AC'],
      [0.97, '#7ED957'],
    ],
  },
}

// --- Geometry (viewBox 0 0 206.008 100.705) — the "gotü" wordmark -----------
// Six letterforms (g/o/t/ü + the two ü-eyes) share one fill; the ring is drawn
// last and is the only path that takes the gradient.
const WORDMARK_LETTERS = [
  // t
  'M132.692 78.2857C132.692 79.7005 132.115 80.7847 130.962 81.5151C129.808 82.2533 128.17 82.5609 126.048 82.4455L123.657 82.2687C113.738 81.5613 108.778 76.2481 108.778 66.3291V47.0216H104.35C102.758 47.0216 101.528 46.6679 100.674 45.9605C99.8206 45.2531 99.39 44.2228 99.39 42.8618C99.39 41.5008 99.8206 40.4705 100.674 39.7631C101.528 39.0557 102.758 38.702 104.35 38.702H108.778V30.5515C108.778 28.9598 109.278 27.6911 110.286 26.7453C111.285 25.7996 112.646 25.3305 114.361 25.3305C116.075 25.3305 117.344 25.8073 118.344 26.7453C119.343 27.6911 119.851 28.9598 119.851 30.5515V38.702H127.379C128.97 38.702 130.2 39.0557 131.054 39.7631C131.907 40.4705 132.338 41.5085 132.338 42.8618C132.338 44.2151 131.907 45.2531 131.054 45.9605C130.2 46.6679 128.97 47.0216 127.379 47.0216H119.851V67.1287C119.851 71.4962 121.858 73.8337 125.871 74.1259L128.263 74.3027C131.215 74.4796 132.692 75.8098 132.692 78.2857Z',
  // g
  'M42.8593 39.2326C43.8589 40.1707 44.3587 41.4394 44.3587 43.0234V77.8552C44.3587 84.614 42.4749 89.7272 38.7149 93.1951C34.9549 96.6629 29.3956 98.4006 22.0525 98.4006C15.6474 98.4006 10.2958 97.1934 6.00523 94.7867C3.8292 93.4949 2.74503 91.934 2.74503 90.1117C2.74503 88.8199 3.05259 87.7896 3.66772 87.0284C4.28286 86.2671 5.09022 85.8827 6.08981 85.8827C6.62036 85.8827 7.26625 86.0134 8.02747 86.2825C8.7887 86.5439 9.58837 86.8592 10.4111 87.2052C12.5256 88.0895 14.4248 88.7584 16.1011 89.2351C17.7773 89.7042 19.6996 89.9425 21.8756 89.9425C29.5802 89.9425 33.4247 86.1826 33.4247 78.6549V73.011C32.133 75.4254 30.2491 77.2862 27.7809 78.6087C25.3127 79.9313 22.4908 80.5925 19.3151 80.5925C15.5551 80.5925 12.2181 79.7006 9.30387 77.9013C6.39737 76.1098 4.11369 73.5954 2.46822 70.3583C0.822739 67.1288 0 63.3919 0 59.1629C0 54.9338 0.807361 51.1815 2.42208 47.9213C4.0368 44.6611 6.31279 42.1314 9.25774 40.3398C12.195 38.5483 15.5475 37.6486 19.3075 37.6486C22.4831 37.6486 25.305 38.3099 27.7732 39.6324C30.2414 40.955 32.1253 42.8234 33.417 45.2301V42.9388C33.417 41.4086 33.9168 40.1784 34.9164 39.2326C35.916 38.2945 37.2385 37.8178 38.884 37.8178C40.5295 37.8178 41.852 38.2868 42.8516 39.2326H42.8593ZM30.4721 68.5975C32.4405 66.3061 33.4247 63.1612 33.4247 59.1629C33.4247 55.1645 32.4405 51.9351 30.4721 49.636C28.5037 47.3447 25.751 46.199 22.2293 46.199C18.7077 46.199 16.0242 47.3447 14.025 49.636C12.0258 51.9274 11.0262 55.103 11.0262 59.1629C11.0262 63.2228 12.0258 66.3061 14.025 68.5975C16.0242 70.8888 18.7538 72.0345 22.2293 72.0345C25.7048 72.0345 28.5037 70.8888 30.4721 68.5975Z',
  // o
  'M63.7968 79.8007C60.4751 77.9784 57.8993 75.364 56.0846 71.9501C54.2623 68.5438 53.355 64.5762 53.355 60.0473C53.355 55.5183 54.2623 51.5661 56.0846 48.1906C57.907 44.8074 60.4751 42.2084 63.7968 40.3861C67.1186 38.5638 70.9862 37.6565 75.3921 37.6565C79.798 37.6565 83.6656 38.5715 86.9873 40.3861C90.309 42.2084 92.8618 44.8074 94.6611 48.1906C96.4527 51.5738 97.3523 55.526 97.3523 60.0473C97.3523 64.5685 96.4527 68.5438 94.6611 71.9501C92.8695 75.3564 90.309 77.9784 86.9873 79.8007C83.6656 81.623 79.798 82.5303 75.3921 82.5303C70.9862 82.5303 67.1186 81.623 63.7968 79.8007ZM83.5041 70.4507C85.388 68.0978 86.326 64.63 86.326 60.0473C86.326 55.4645 85.388 52.0659 83.5041 49.6823C81.6203 47.2986 78.9214 46.1145 75.3921 46.1145C71.8628 46.1145 69.1485 47.3063 67.2339 49.6823C65.3193 52.0659 64.3658 55.5183 64.3658 60.0473C64.3658 64.5762 65.3039 68.0978 67.1878 70.4507C69.0716 72.8036 71.7705 73.98 75.2998 73.98C78.8291 73.98 81.6203 72.8036 83.5041 70.4507Z',
  // ü body
  'M175.266 43.1462C174.267 42.2004 172.937 41.7314 171.283 41.7314C169.63 41.7314 168.216 42.2081 167.208 43.1462C166.209 44.092 165.701 45.3607 165.701 46.9523V62.669C165.701 65.7369 165.078 68.0129 163.84 69.4892C162.61 70.9579 160.672 71.696 158.035 71.7037C155.398 71.7037 153.46 70.9579 152.23 69.4892C150.992 68.0129 150.369 65.7369 150.369 62.669V46.9523C150.369 45.3607 149.869 44.092 148.862 43.1462C147.855 42.2004 146.501 41.7314 144.787 41.7314C143.072 41.7314 141.803 42.2081 140.804 43.1462C139.796 44.092 139.297 45.3607 139.297 46.9523V63.1457C139.297 75.0485 147.401 82.0918 157.997 82.1148C157.997 82.1148 158.02 82.1148 158.027 82.1148C158.035 82.1148 158.05 82.1148 158.058 82.1148C168.654 82.0841 176.758 75.0408 176.758 63.138V46.9446C176.758 45.353 176.258 44.0843 175.251 43.1385L175.266 43.1462Z',
  // ü left eye
  'M144.764 35.8493C147.609 35.8493 149.915 33.5428 149.915 30.6976C149.915 27.8524 147.609 25.5459 144.764 25.5459C141.918 25.5459 139.612 27.8524 139.612 30.6976C139.612 33.5428 141.918 35.8493 144.764 35.8493Z',
  // ü right eye
  'M170.853 35.7109C173.698 35.7109 176.005 33.4044 176.005 30.5592C176.005 27.7139 173.698 25.4074 170.853 25.4074C168.008 25.4074 165.701 27.7139 165.701 30.5592C165.701 33.4044 168.008 35.7109 170.853 35.7109Z',
]
const WORDMARK_RING =
  'M166.731 1.33259C160.565 -0.228304 154.252 -0.397465 147.993 0.717461C146.324 1.00965 144.671 1.38642 143.041 1.83239C137.72 3.27795 132.622 5.53087 127.994 8.52964C124.918 10.5211 122.05 12.8278 119.466 15.4268C118.067 16.8339 116.652 18.81 117.436 20.6323C118.144 22.2778 120.374 22.7699 122.065 22.1855C123.757 21.6012 125.056 20.2556 126.363 19.0407C138.012 8.199 155.936 4.64662 170.837 10.2289C185.739 15.8112 196.919 30.2668 198.58 46.0911C200.241 61.9154 192.298 78.3701 178.873 86.9205C165.447 95.4708 147.178 95.7092 133.537 87.5125C131.915 86.536 130.262 85.4211 128.378 85.2673C126.494 85.1135 124.326 86.3822 124.303 88.2738C124.287 89.85 125.671 91.0496 126.963 91.9492C141.988 102.329 162.818 103.598 178.996 95.1248C195.174 86.6514 205.985 68.8048 206.008 50.5431C206.008 45.4837 205.185 40.4396 203.647 35.6262C202.109 30.8128 199.826 26.1685 196.95 21.9702C194.074 17.772 190.599 13.9966 186.623 10.8287C184.301 8.97561 181.787 7.39164 179.134 6.05373C175.22 4.08531 171.03 2.43214 166.731 1.34797V1.33259Z'

// --- Geometry (viewBox 0 0 78.4055 80) — the circular monogram -------------
// The ring is drawn first (gradient slot); the ü + two eyes share the feature
// fill. A purpose-built, self-contained mark — NOT a crop of the wordmark.
const MONOGRAM_RING =
  'M72.968 40.0001C72.968 20.9121 57.4937 5.43781 38.4056 5.43775C22.8096 5.43775 9.62112 15.766 5.32167 29.9638C4.88648 31.4009 3.36852 32.2131 1.93145 31.7779C0.494482 31.3427 -0.317706 29.825 0.117357 28.388C5.09203 11.9604 20.348 0 38.4056 0C60.4967 6.23684e-05 78.4055 17.909 78.4055 40.0001C78.4055 62.0913 60.4967 79.9999 38.4056 80C20.6378 80 5.58028 68.4167 0.36414 52.3961C-0.10064 50.9684 0.679863 49.4341 2.10752 48.9692C3.53527 48.5044 5.06951 49.2852 5.53437 50.7129C10.043 64.5605 23.0606 74.5625 38.4056 74.5625C57.4937 74.5625 72.968 59.0882 72.968 40.0001Z'
const MONOGRAM_FEATURES = [
  // ü body
  'M52.7945 33.547C51.9653 32.7674 50.8657 32.3753 49.5003 32.3753C48.1349 32.3753 46.9632 32.7674 46.134 33.547C45.3049 34.3266 44.8903 35.3766 44.8903 36.6969V49.6933C44.8903 53.2714 43.2004 56.7999 39.3069 57.2776C34.6608 57.8454 32.2138 53.8167 32.2138 49.6933V36.6969C32.2138 35.3766 31.7993 34.3311 30.9701 33.547C30.1409 32.7674 29.0188 32.3753 27.6038 32.3753C26.1888 32.3753 25.1388 32.7674 24.3097 33.547C23.4805 34.3266 23.0659 35.3766 23.0659 36.6969V50.0809C23.0659 59.9228 29.7669 65.7405 38.5273 65.7676C38.5363 65.7676 38.5453 65.7676 38.5543 65.7676C38.5633 65.7676 38.5723 65.7676 38.5813 65.7676C47.3417 65.745 54.0427 59.9228 54.0427 50.0809V36.6969C54.0427 35.3766 53.6281 34.3311 52.799 33.547H52.7945Z',
  // left eye
  'M27.5766 27.5129C29.9285 27.5129 31.8351 25.6063 31.8351 23.2544C31.8351 20.9025 29.9285 18.9959 27.5766 18.9959C25.2247 18.9959 23.3181 20.9025 23.3181 23.2544C23.3181 25.6063 25.2247 27.5129 27.5766 27.5129Z',
  // right eye
  'M49.1441 27.3958C51.496 27.3958 53.4026 25.4892 53.4026 23.1373C53.4026 20.7853 51.496 18.8787 49.1441 18.8787C46.7922 18.8787 44.8856 20.7853 44.8856 23.1373C44.8856 25.4892 46.7922 27.3958 49.1441 27.3958Z',
]

function Gradient({id, def}: {id: string; def: GradientDef}) {
  return (
    <defs>
      <linearGradient
        id={id}
        x1={def.x1}
        y1={def.y1}
        x2={def.x2}
        y2={def.y2}
        gradientUnits="userSpaceOnUse"
      >
        {def.stops.map(([offset, color]) => (
          <stop key={`${offset}-${color}`} offset={offset} stopColor={color} />
        ))}
      </linearGradient>
    </defs>
  )
}

export const Logo = forwardRef<SVGSVGElement, LogoProps>(
  (
    {
      mark = 'full',
      appearance = 'gradient',
      tone,
      variant,
      className,
      'aria-label': ariaLabel = 'GoTu',
      width = '100%',
      height,
      ...props
    },
    ref,
  ) => {
    const uid = useId()
    const gradientId = `logo-gradient-${uid}`

    const resolvedTone: LogoTone = tone ?? variant ?? 'dark'
    const isGradient = appearance === 'gradient'
    const solidColor = resolvedTone === 'dark' ? NEAR_BLACK : WHITE
    const ringFill = isGradient ? `url(#${gradientId})` : solidColor

    const commonProps = {
      ref,
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg',
      width,
      height,
      className: cn(className),
      ...props,
    }

    if (mark === 'monogram') {
      // ü + eyes: teal on dark gradient, white on light gradient, else the solid tone.
      const featureFill = isGradient ? (resolvedTone === 'dark' ? FEATURE_TEAL : WHITE) : solidColor
      return (
        <svg viewBox="0 0 78.4055 80" role="img" aria-label={ariaLabel} {...commonProps}>
          <path d={MONOGRAM_RING} fill={ringFill} />
          {MONOGRAM_FEATURES.map((d) => (
            <path key={d} d={d} fill={featureFill} />
          ))}
          {isGradient && <Gradient id={gradientId} def={MONOGRAM_GRADIENT[resolvedTone]} />}
        </svg>
      )
    }

    // Wordmark letterforms: deep teal on dark gradient, near-black on dark solid, white on light.
    const letterFill = isGradient && resolvedTone === 'dark' ? DEEP_TEAL : solidColor
    return (
      <svg viewBox="0 0 206.008 100.705" role="img" aria-label={ariaLabel} {...commonProps}>
        {WORDMARK_LETTERS.map((d) => (
          <path key={d} d={d} fill={letterFill} />
        ))}
        <path d={WORDMARK_RING} fill={ringFill} />
        {isGradient && <Gradient id={gradientId} def={WORDMARK_GRADIENT[resolvedTone]} />}
      </svg>
    )
  },
)

Logo.displayName = 'Logo'
